import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogActions } from '@angular/material/dialog';
import { Building } from '../model/building.model';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import localeFr from 'dayjs/esm/locale/fr';
import localeDe from 'dayjs/esm/locale/de';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { MatChipListbox, MatChipOption, MatChipAvatar } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { ValidateButtonComponent } from '../../shared/buttons/validate-button.component';
import { SecondaryButtonComponent } from '../../shared/buttons/secondary-button.component';

@Component({
  selector: 'sibat-batch-status-edit-dialog',
  templateUrl: './batch-status-edit-dialog.component.html',
  styles: [
    `
      .status-edit-content {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 15px;
      }
      .chip-status {
        display: flex;
        flex-direction: row;
        width: 75%;
        padding-top: 15px;
        padding-bottom: 15px;
      }
      .custom-chip {
        font-size: 14px;
        height: 35px;
        width: 200px;
        line-height: 32px;
        padding: 0 12px;
        margin: 14px;
        border-radius: 16px;
        background-color: white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

        &.selected {
          background-color: white;
          color: #006600;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      }
    `,
  ],
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatChipListbox,
    MatChipOption,
    MatIcon,
    MatChipAvatar,
    MatFormField,
    MatLabel,
    MatInput,
    CdkTextareaAutosize,
    MatDialogActions,
    ValidateButtonComponent,
    SecondaryButtonComponent,
    TranslocoPipe,
  ],
})
export class BatchStatusEditDialogComponent implements OnInit {
  form: FormGroup;
  readonly count: number;
  readonly building?: Building;
  statusDate?: string;
  selectedStatus: string | null = null;

  buildingStatusList: { key: string; label: string }[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { count: number; building: Building },
    private dialogRef: MatDialogRef<BatchStatusEditDialogComponent, Building>,
    private translocoService: TranslocoService,
    private localeService: TranslocoLocaleService,
    private fb: FormBuilder
  ) {
    dayjs.extend(relativeTime);
    this.count = this.data.count ?? 1;
    this.building = this.data.building;

    this.form = this.fb.group({
      status: [null, Validators.required],
      justification: '',
    });
  }

  ngOnInit(): void {
    this.loadBuildingStatuses();
    this.statusDate = this.building?.buildingStatus?.date ? this.fromNow(this.building.buildingStatus.date) : '';
  }

  addStatus(status: string) {
    this.selectedStatus = status;
    this.form.get('status')?.setValue(status);
  }

  save() {
    if (this.form.valid) {
      this.dialogRef.close(this.form.value);
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  fromNow = (date: string): string =>
    dayjs(date)
      .locale(this.localeService.getLocale().includes('fr') ? localeFr : localeDe)
      .fromNow();

  loadBuildingStatuses() {
    const construction = this.translocoService.translate('building.statusChoice.constructed');
    const projet = this.translocoService.translate('building.statusChoice.project');
    const travaux = this.translocoService.translate('building.statusChoice.underConstruction');
    const exploitation = this.translocoService.translate('building.statusChoice.exploitation');
    const demoli = this.translocoService.translate('building.statusChoice.demolished');
    this.buildingStatusList.push(
      { key: 'CONSTRUCTED', label: construction },
      { key: 'PROJECT', label: projet },
      { key: 'UNDER_CONSTRUCTION', label: travaux },
      { key: 'EXPLOITATION', label: exploitation },
      { key: 'DEMOLISHED', label: demoli }
    );
  }
}
